import type { AssuranceLevel, AuthSession, PermissionGrant, ScopeGrant } from './types';
import type { TenantTier } from '@cguard/types';

/** Server capability that preserves regulator-style cross-tenant reach. */
export const CROSS_TENANT_SCOPE_PERMISSION = 'realtime.cross-tenant-subscribe';

export interface RoleGuardOptions {
  /** Allowed server-resolved roles. The caller must have at least one. */
  anyOf: ReadonlyArray<string>;
  /** @deprecated Prefer a permission plus an explicit scope check. */
  tier?: TenantTier;
}

/**
 * Transitional role predicate. Roles are read only from `/v1/identity/me`
 * through `roleNames`. New app gates should use {@link hasPermission}.
 */
export function hasRole(session: AuthSession | null, options: RoleGuardOptions): boolean {
  if (!session) return false;
  if (options.tier && tierRank(session.tenant.tier) < tierRank(options.tier)) return false;
  return options.anyOf.some((role) => session.roleNames?.includes(role) === true);
}

/** True when `/me` granted the permission, regardless of its required LoA. */
export function hasPermission(session: AuthSession | null, permissionKey: string): boolean {
  return findPermission(session, permissionKey) !== null;
}

/**
 * LoA-aware permission predicate for elevated UX flows. The current assurance
 * is supplied by the ceremony state; it is never inferred from token claims.
 */
export function hasPermissionAtLoa(
  session: AuthSession | null,
  permissionKey: string,
  currentLoa: AssuranceLevel,
): boolean {
  const permission = findPermission(session, permissionKey);
  return permission !== null && permission.requiredLoa <= currentLoa;
}

const TIER_RANK: Record<TenantTier, number> = { sira: 3, ssp: 2, ssu: 1 };
function tierRank(tier: TenantTier): number {
  return TIER_RANK[tier];
}

/** @deprecated Prefer {@link requirePermission}. */
export function requireRole(session: AuthSession | null, options: RoleGuardOptions): AuthSession {
  if (session === null || !hasRole(session, options)) {
    const need = options.anyOf.join(' | ');
    throw forbidden(`Forbidden: needs role ${need}`);
  }
  return session;
}

/** Throws unless `/me` granted the requested permission. */
export function requirePermission(session: AuthSession | null, permissionKey: string): AuthSession {
  if (session === null || !hasPermission(session, permissionKey)) {
    throw forbidden(`Forbidden: needs permission ${permissionKey}`);
  }
  return session;
}

export interface ScopeGuardOptions {
  sspId?: string;
  ssuId?: string;
  siteId?: string;
}

/**
 * Pure predicate over server-issued scope grants. The former SIRA tier bypass
 * is preserved only when `/me` grants the explicit cross-tenant capability.
 */
export function hasScope(session: AuthSession | null, options: ScopeGuardOptions): boolean {
  if (!session) return false;
  if (hasPermission(session, CROSS_TENANT_SCOPE_PERMISSION)) return true;
  return (session.scopeGrants ?? []).some((grant) => scopeGrantMatches(grant, options));
}

export function requireScope(session: AuthSession | null, options: ScopeGuardOptions): AuthSession {
  if (session === null || !hasScope(session, options)) {
    throw forbidden('Forbidden: out of tenant scope');
  }
  return session;
}

function findPermission(
  session: AuthSession | null,
  permissionKey: string,
): PermissionGrant | null {
  if (!session || permissionKey.length === 0) return null;
  return (
    session.permissions?.find((permission) => permission.permissionKey === permissionKey) ?? null
  );
}

function scopeGrantMatches(grant: ScopeGrant, options: ScopeGuardOptions): boolean {
  const type = grant.scopeType.toLowerCase();
  const matchesSsp =
    options.sspId === undefined ||
    grant.granteeSspId === options.sspId ||
    (type === 'ssp' && grant.scopeId === options.sspId);
  const matchesSsu =
    options.ssuId === undefined ||
    grant.granteeSsuId === options.ssuId ||
    (type === 'ssu' && grant.scopeId === options.ssuId);
  const matchesSite =
    options.siteId === undefined || (type === 'site' && grant.scopeId === options.siteId);
  return matchesSsp && matchesSsu && matchesSite;
}

function forbidden(message: string): Error {
  return Object.assign(new Error(message), { status: 403 });
}
